import { useSharedAPI } from "@certn/api";
import {
  SEARCHABLE_INPUT_CUSTOM_KEY,
  SearchableInput,
} from "@certn/cascadia-react";
import { slugifyPath } from "@certn/utils";
import { useJsonForms } from "@jsonforms/react";
import get from "lodash.get";
import { useContext, useEffect, useRef, useState } from "react";

import { CascadiaJsonFormContext } from "../CascadiaJsonFormContext";
import { SummaryItem } from "../components/SummaryItem";
import {
  CascadiaControlProps,
  withCascadiaControlProps,
} from "../wrappers/withCascadiaControlProps";

export function EducationInstitutionControl(props: CascadiaControlProps) {
  const context = useJsonForms();
  const { isSummary, formID } = useContext(CascadiaJsonFormContext);

  const country = get(
    context?.core?.data,
    props.uischema?.options?.relatedCountryField
  );

  const { data: apiInstitutions, isLoading } = useSharedAPI(
    "/api/internal/shared/education-institutions/",
    {
      country: country || null,
    }
  );
  const prevCountry = useRef(country);
  useEffect(() => {
    // Don't reset if the applicant goes back to the first page
    if (prevCountry.current !== country) {
      props.setValue(undefined);
      prevCountry.current = country;
    }
  }, [country]);

  const elements = (props.uischema as any)?.elements;
  const institution = props.data;
  // Separate to props.data because if a user types "bimm" for example, we want to immediately update the JSON form data with "BIMM" and the key
  // but don't want to render it until they click off because that causes the popover to unexpectedly close.
  // If CHEESE-256 was fixed, we could exclusively use `onSelectionChange` instead
  // Although we probably should have made this claim just a string on the backend and do the matching to known institutions there instead, maybe we can do that as part of adding quickscreen support
  const [displayedKey, setDisplayedKey] = useState<string | null | undefined>(
    apiKeyToInputKey(institution?.internal_id)
  );

  const institutionItems = (apiInstitutions || []).map(
    ({ internal_id, display_name }) => ({
      key: internal_id,
      label: display_name,
    })
  );

  if (isSummary) {
    return <SummaryItem label={props.label}>{institution?.name}</SummaryItem>;
  }

  return (
    <SearchableInput
      description={props.description}
      placeholder=""
      errors={props.errors}
      isLoading={isLoading}
      isRequired={Boolean(props.required)}
      items={institutionItems} // if institutionItems is empty, this will behave like a TextFieldControl
      label={elements?.[0].label}
      name={props.path}
      onBlur={props.setTouched}
      isInvalid={props.invalid}
      defaultCustomValue={institution?.name}
      // https://certn.atlassian.net/browse/CHEESE-256 -- maybe should be onSelectionChange instead if it worked?
      onInputChange={(key, value) => {
        setTimeout(() => {
          if (key === null) {
            props.setValue(undefined);
            return;
          }

          props.setValue({
            internal_id: inputKeyToApiKey(key),
            name: value,
          });
        });
      }}
      onSelectionChange={(key, value) => {
        // Changing null/blank string to undefined triggers the "is required" schema validation error,
        // rather than a "must be string"/"must be at least 1 character" message
        props.setValue?.(
          key === null
            ? undefined
            : {
                internal_id: inputKeyToApiKey(key),
                name: value,
              }
        );
        setDisplayedKey(key);
      }}
      selectedKey={displayedKey ?? null}
      baseTestID={`${formID ? `${formID}-` : ""}${slugifyPath(props.path)}-searchable-select`}
      minInputCharactersToShowPopover={2}
    />
  );
}

EducationInstitutionControl.displayName = "EducationInstitutionControl";

export const EducationInstitutionRenderer = withCascadiaControlProps(
  EducationInstitutionControl
);

function inputKeyToApiKey(key: string) {
  return key === SEARCHABLE_INPUT_CUSTOM_KEY ? "" : key;
}
function apiKeyToInputKey(key: string | null | undefined) {
  return key === undefined // Unset
    ? null
    : key === "" || key === null // Custom value
      ? SEARCHABLE_INPUT_CUSTOM_KEY
      : key;
}
